import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { APIError } from '../utils/errorHandler';

const ApiErrorContext = createContext();

const initialState = {
  error: null,
  isVisible: false,
  level: 'page', // 'page' | 'component'
  retryFunction: null,
  onGoHome: null
};

const apiErrorReducer = (state, action) => {
  switch (action.type) {
    case 'SHOW_ERROR':
      return {
        ...state,
        error: action.payload.error,
        isVisible: true,
        level: action.payload.level || 'page',
        retryFunction: action.payload.retryFunction || null,
        onGoHome: action.payload.onGoHome || null
      };
    
    case 'HIDE_ERROR':
      return {
        ...state,
        error: null,
        isVisible: false,
        retryFunction: null,
        onGoHome: null
      };
    
    case 'CLEAR_ERROR':
      return initialState;
    
    default:
      return state;
  }
};

export const ApiErrorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(apiErrorReducer, initialState);

  const showError = useCallback((error, options = {}) => {
    const apiError = error instanceof APIError ? error : new APIError(
      error.status || 500,
      error.message || 'An unexpected error occurred',
      error.details || null,
      error
    );

    dispatch({
      type: 'SHOW_ERROR',
      payload: {
        error: apiError,
        level: options.level || 'page',
        retryFunction: options.onRetry || null,
        onGoHome: options.onGoHome || null
      }
    });
  }, []);

  const hideError = useCallback(() => {
    dispatch({ type: 'HIDE_ERROR' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const handleRetry = useCallback(() => {
    if (state.retryFunction) {
      hideError();
      state.retryFunction();
    } else {
      window.location.reload();
    }
  }, [state.retryFunction, hideError]);

  const handleGoHome = useCallback(() => {
    if (state.onGoHome) {
      clearError();
      state.onGoHome();
    } else {
      clearError();
      window.location.href = '/';
    }
  }, [state.onGoHome, clearError]);

  const value = {
    error: state.error,
    isVisible: state.isVisible,
    level: state.level,
    showError,
    hideError,
    clearError,
    handleRetry,
    handleGoHome
  };

  return (
    <ApiErrorContext.Provider value={value}>
      {children}
    </ApiErrorContext.Provider>
  );
};

export const useApiError = () => {
  const context = useContext(ApiErrorContext);
  if (!context) {
    throw new Error('useApiError must be used within an ApiErrorProvider');
  }
  return context;
};